import {handleFormError, handleSubmit} from 'app/client/lib/formUtils';
import {buildURL} from 'app/client/lib/urlUtils';
import {cssLink} from 'app/client/ui2018/links';
import {textInput} from 'app/client/ui/inputs';
import {LoginAPI} from 'app/client/ui/LoginAPI';
import * as css from 'app/client/ui/LoginPagesCss';
import {buildOAuthFormTitle} from 'app/client/ui/OAuthForms';
import {ApiError} from 'app/common/ApiError';
import {ChallengeRequired} from 'app/common/LoginAPI';
import {LoginPageConfig} from 'app/common/LoginPageConfig';
import {getGristConfig} from 'app/common/urlUtils';
import {dom, IDisposableOwner, makeTestId, Observable, styled} from 'grainjs';

const testId = makeTestId('test-lp-');

interface LoginFormOptions {
  email: Observable<string>;
  password: Observable<string>;
  loginAPI: LoginAPI;
  onChallengeRequired: (challenge: ChallengeRequired) => void;
  onEmailVerificationRequired: () => void;
}

/**
 * Builds a login form for an existing Grist account. Supports logging in with
 * Google, or email and password.
 *
 * On successful submission of the email/password form, if a user does not have
 * any 2FA methods enabled for their account, they will be redirected to the
 * main Grist application. Otherwise, `onChallengeRequired` will be called, which
 * should advance the flow to the second factor verification step.
 *
 * If the user associated with the email is unverified, `onEmailVerificationRequired`
 * will be called, which changes the flow to email verification.
 *
 * Google Sign-In redirects to Google's OAuth flow, which further redirects to the
 * main Grist application on successful authentication with Google.
 */
export function buildLoginForm(owner: IDisposableOwner, options: LoginFormOptions) {
  const {
    email: emailObs,
    password: passwordObs,
    loginAPI,
    onChallengeRequired,
    onEmailVerificationRequired
  } = options;

  const error = Observable.create(owner, '');
  const pending = Observable.create(owner, false);

  return dom.frag(
    buildLoginFormTitle(),
    dom('form',
      css.googleButton(css.googleLogo(), 'Sign in with Google'),
      handleSubmit({
        onSubmit: () => loginAPI.signInWithGoogle(),
      }),
      testId('google-sign-in'),
    ),
    css.loginMethodsSeparator(css.horizontalLine(), css.lightText('or'), css.horizontalLine()),
    dom('form',
      dom.maybe(error, (err) => css.centeredFormError(err, testId('form-error'))),
      cssLabelAndInput(
        css.formLabel('Email', {for: 'email'}),
        textInput(emailObs,
          {
            id: 'email',
            name: 'email',
            autocomplete: 'username',
            placeholder: 'name@host.com',
            type: 'email',
            required: 'true',
          },
          (el) => { setTimeout(() => el.focus(), 10); },
          testId('email-input'),
        ),
      ),
      cssPassword(
        css.formLabel('Password', {for: 'current-password'}),
        textInput(passwordObs,
          {
            id: 'current-password',
            name: 'password',
            autocomplete: 'current-password',
            placeholder: 'Enter password',
            type: 'password',
            required: 'true',
          },
          testId('password-input'),
        ),
      ),
      cssForgotPassword(cssLink('Forgot your password?',
        {href: buildURL('/forgot-password').href},
        testId('forgot-password'),
      )),
      cssSignInButton('Sign in',
        dom.boolAttr('disabled', pending),
        testId('sign-in')
      ),
      css.centeredText("Don't have an account? ",
        cssLink('Sign up',
          {href: buildURL('/signup').href},
          testId('sign-up'),
        ),
      ),
      handleSubmit({
        pending,
        onSubmit: ({email, password}) => loginAPI.logIn(email, password),
        onSuccess: (result) => {
          if (result.isChallengeRequired === false) {
            window.location.assign(result.redirectUrl);
          } else {
            onChallengeRequired(result);
          }
        },
        onError: (e) => {
          if (e instanceof ApiError && e.details?.code === 'UserNotConfirmed') {
            onEmailVerificationRequired();
          } else {
            handleFormError(e, error);
          }
        },
      }),
    ),
  );
}

function buildLoginFormTitle() {
  const {oauth: {client} = {}} = getGristConfig() as LoginPageConfig;
  if (client) {
    return buildOAuthFormTitle({client, variant: "login"});
  } else {
    return [
      css.flexJustifyCenter(css.gristLogo()),
      css.centeredFormHeading('Welcome back', testId('heading')),
      css.centeredFormSubheading('Please sign in to continue to your Grist account.'),
    ];
  }
}

const cssLabelAndInput = styled('div', `
  margin-bottom: 20px;
`);

const cssSignInButton = styled(css.bigPrimaryButton, `
  width: 100%;
  margin-bottom: 24px;
`);

const cssPassword = styled(cssLabelAndInput, `
  margin-bottom: 16px;
`);

const cssForgotPassword = styled('div', `
  margin-bottom: 32px;
`);
